import {
  parkActiveDepartment, activateDepartment, rememberDepartment, setPendingDeepLink,
  forgetDepartment, parkedToken, activeDepartmentUrl, listDepartments
} from './departments'

/**
 * Switching departments on the native app. The bookkeeping is in
 * departments.js; this adds the two things kept outside localStorage and
 * restarts the app so every context starts clean on the new server.
 *
 * - The station PIN token has a copy in Capacitor Preferences, which
 *   getDeviceToken() falls back to. Left there, the next department would pick
 *   up the last one's station registration.
 * - Face ID / fingerprint sign-in is stored per department already (see
 *   biometricServer in native.js), so it needs nothing here.
 */

const clearPinCopy = async () => {
  try {
    const { Preferences } = await import('@capacitor/preferences')
    await Preferences.remove({ key: 'pinDeviceToken' })
  } catch { /* not native */ }
}

const restorePinCopy = async () => {
  const token = localStorage.getItem('pinDeviceToken')
  if (!token) return
  try {
    const { Preferences } = await import('@capacitor/preferences')
    await Preferences.set({ key: 'pinDeviceToken', value: token })
  } catch { /* not native */ }
}

/** Switch to a department already on this device, staying signed in to the others. */
export async function switchToDepartment(url, { deepLink } = {}) {
  if (!listDepartments().some(d => d.url === url)) return
  if (deepLink) setPendingDeepLink(deepLink)
  parkActiveDepartment()
  await clearPinCopy()
  activateDepartment(url)
  await restorePinCopy()
  window.location.replace('/')
}

/** Go to the connect screen to add a department, keeping the current one. */
export async function addDepartment() {
  parkActiveDepartment()
  await clearPinCopy()
  window.location.replace('/')
}

/** Called by the connect screen once a server checks out. */
export async function connectDepartment({ url, name, code }) {
  rememberDepartment({ url, name, code })
  activateDepartment(url)
  await restorePinCopy()
}

/**
 * Remove a department that isn't active: stop its notifications (with its own
 * parked sign-in) and forget it. Never throws.
 */
export async function removeDepartment(url, deviceToken) {
  if (url === activeDepartmentUrl()) return
  const token = parkedToken(url)
  if (token && deviceToken) {
    try {
      await Promise.race([
        fetch(`${url}/api/push/unregister`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ token: deviceToken })
        }),
        new Promise(resolve => setTimeout(resolve, 3000))
      ])
    } catch { /* best effort */ }
  }
  forgetDepartment(url)
}
